<template>
  <v-card>
    <v-card-title class="justify-center text-h6 text-break text-center">{{
      title
    }}</v-card-title>
    <v-card-text class="px-0">
      <div class="d-flex justify-center align-center mt-2">
        <v-btn-toggle
          v-model="selectedType"
          dense
          rounded
          class="ma-auto"
          @change="handleTypeChange"
        >
          <v-btn value="PC" small color="primary" class="text-none">PC</v-btn>
          <v-btn value="MG" small color="primary" class="text-none">MG</v-btn>
          <v-btn value="VR" small color="primary" class="text-none">VR</v-btn>
        </v-btn-toggle>
      </div>
      <div class="player" v-if="!videoEnded">
        <v-alert
          v-if="writeError"
          type="warning"
          outlined
          dense
          class="ma-4 text-body-2"
        >
          <div class="font-weight-medium">Просмотр не засчитан</div>
          <div>{{ writeError }}</div>
          <div class="mt-1">Закройте окно и откройте сессию заново.</div>
        </v-alert>
        <n-s-player
          v-else-if="effectiveVideoData && isVideoAvailable"
          :key="`${selectedType}-${effectiveVideoData}`"
          :video_data="effectiveVideoData"
          :session_id="sessionId"
          :view_progress="viewProgress"
          @videoEnded="videoEndedHandler"
          @updateViewProgress="updateViewProgress"
        />
        <video-availability-message
          v-else-if="!isVideoAvailable && playbackAvailabilityTime"
          :playback-availability-time="playbackAvailabilityTime"
          @video-available="handleVideoAvailable"
        />
      </div>
      <div v-else class="text-center pl-4 mt-4">
        <h2>Спасибо за просмотр!</h2>
      </div>
      <v-card class="text-center d-flex flex-column justify-center" flat>
        <v-rating
          v-if="!rated"
          color="yellow darken-3"
          background-color="yellow darken-3"
          half-increments
          hover
          large
          class="ma-auto mt-4"
          @input="ratingChange($event)"
        >
        </v-rating>
        <div v-if="rated" class="mt-4">Спасибо за отзыв!</div>
        <div class="text-center text-sm-caption pl-4 mt-4" v-if="rating">
          <template v-if="rating.count">
            Рейтинг: {{ rating.average.toFixed(1) }} / Оценок:
            {{ rating.count }}
          </template>
          <template v-else>Оценок пока нет</template>
        </div>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import NSPlayer from "@/components/ns-player/NSPlayer";
import VideoAvailabilityMessage from "@/components/VideoAvailabilityMessage.vue";
import ApiService from "@/api/ApiService";

// Насколько раньше срока клинике открывается сессия. Дубль
// STAFF_GRACE_HOURS из upmind-backend/src/helpers/sessionAccess.js:
// сервер проверяет таймер сам и на расхождение отвечает 409.
const STAFF_GRACE_HOURS = 38;

/**
 * Отказ бэкенда по результату вызова ApiService.
 *
 * ApiService.handleError не бросает исключение, а возвращает
 * { success: false, status, data } — поэтому 409 приходил сюда обычным
 * результатом, и try/catch вокруг вызова не срабатывал никогда. Сотрудник
 * досматривал сессию до конца, не получая ни одного признака, что она не
 * засчитана: заказ B000403, 26.08.2026 — одну и ту же сессию так посмотрели
 * четыре раза подряд.
 *
 * @returns {{ fatal: boolean, text: string } | null}
 */
function rejectionOf(result) {
  if (result?.success !== false) return null;

  // 409 — сервер разобрался и отказал: смотреть дальше бессмысленно.
  if (result.status === 409) {
    return {
      fatal: true,
      text: humanizeTime(result.data?.error || "Сессия сейчас недоступна."),
    };
  }

  // Сеть или 5xx: просмотр мог не сохраниться, но это не приговор.
  return { fatal: false, text: "Не удалось сохранить просмотр." };
}

/**
 * Вызов обработчика, переданного через modalFormComponent. Свой отказ он
 * возвращает результатом, но упасть тоже не должен: необработанный reject
 * из обработчика плеера — это то же молчание, от которого мы уходим.
 */
async function callSafely(handler, payload) {
  try {
    return await handler(payload);
  } catch (error) {
    console.error("Ошибка при обращении к серверу:", error);
    return { success: false, message: error.message };
  }
}

/**
 * Время в тексте отказа приходит в ISO с Z, а клиника сидит в своём часовом
 * поясе — показывать «17:59Z» значит называть время на три часа раньше.
 */
function humanizeTime(text) {
  return text.replace(/\d{4}-\d{2}-\d{2}T[\d:.]+Z/g, (iso) =>
    new Date(iso).toLocaleString("ru-RU", {
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    })
  );
}

export default {
  name: "SessionForm",
  components: { NSPlayer, VideoAvailabilityMessage },
  data() {
    return {
      rated: false,
      selected: [],
      videoEnded: false,
      // Текст отказа бэка: пока он стоит, плеер не показываем.
      writeError: null,
      selectedType: this.modalFormComponent?.selected_type || "PC",
      // Свежий агрегат из ответа бэка на оценку.
      ratingOverride: null,
    };
  },
  computed: {
    ...mapGetters("modals", ["modalFormComponent"]),
    ...mapGetters("products", ["products"]),

    rating() {
      if (this.ratingOverride) return this.ratingOverride;
      if (!this.product) return null;
      // NUMERIC из Postgres приезжает строкой — приводим явно.
      return {
        average:
          this.product.rating_avg != null
            ? Number(this.product.rating_avg)
            : null,
        count: Number(this.product.rating_count) || 0,
      };
    },
    product() {
      return this.products.find((product) => {
        return (
          product.kinescope_id === this.item.kinescope_id[this.selectedType]
        );
      });
    },
    effectiveVideoData() {
      return this.modalFormComponent?.video_data ?? this.product?.kinescope_id;
    },
    sessionId() {
      const provided = this.modalFormComponent?.session_id;
      if (provided) return provided;
      return ["company-patient", "session-by-url", "patient-sessions"].includes(
        this.$route.name
      )
        ? `${this.item?.id}${this.patient_id ?? ""}`
        : undefined;
    },
    title() {
      return this.modalFormComponent?.title;
    },
    desc() {
      return this.modalFormComponent?.desc;
    },
    item() {
      return this.modalFormComponent?.item;
    },
    patient_id() {
      return this.modalFormComponent?.patient_id;
    },
    viewProgress() {
      return this.modalFormComponent?.item?.viewProgress;
    },
    onVideoEndedExternal() {
      return this.modalFormComponent?.onVideoEnded;
    },
    onUpdateViewProgressExternal() {
      return this.modalFormComponent?.onUpdateViewProgress;
    },
    playbackAvailabilityTime() {
      return this.modalFormComponent?.item?.playback_availability_time;
    },
    // Клиника ставит сессию на ближайшем приёме, а не ровно через
    // patientSessionTimeout, поэтому сессия открывается здесь на 38 часов
    // раньше срока. То же число зашито в бэкенде (STAFF_GRACE_HOURS в
    // upmind-backend/src/helpers/sessionAccess.js) — с 13.08.2026 он проверяет
    // таймер сам и на расхождение отвечает 409, а просмотр уходит в никуда.
    // Менять только вместе с ним.
    isVideoAvailable() {
      if (!this.playbackAvailabilityTime) return true;
      const availableAt = new Date(this.playbackAvailabilityTime);
      const now = new Date();
      if (availableAt <= now) return true;
      const hoursRemaining = (availableAt - now) / (1000 * 60 * 60);
      return hoursRemaining < STAFF_GRACE_HOURS;
    },
  },
  methods: {
    ...mapActions("products", ["request_products"]),
    doPlaySession() {},
    handleTypeChange() {
      this.$emit && this.$emit("type-change", this.selectedType);
      const handler = this.modalFormComponent?.onTypeChange;
      if (typeof handler === "function") {
        try {
          handler({
            type: this.selectedType,
            patient_id: this.patient_id,
            item: this.item,
          });
        } catch (error) {
          console.error("Ошибка при смене типа воспроизведения:", error);
        }
      }
    },
    async ratingChange(value) {
      // Оценка уходит на визит, а не PUT'ом на продукт: агрегат считает бэк
      // по таблице фактов (см. N9 в DB_REFACTORING.md).
      if (!this.item?.public_uuid) {
        console.error("Ошибка при оценке: visit public_uuid не задан");
        return;
      }
      this.rated = true;
      try {
        const { data } = await ApiService.post(
          `v1/patients/visits/${this.item.public_uuid}/rating`,
          { score: value }
        );
        this.ratingOverride = data;
      } catch (e) {
        console.error("Ошибка при отправке оценки:", e);
      }
    },
    async videoEndedHandler(data) {
      if (typeof this.onVideoEndedExternal === "function") {
        const viewPercent = typeof data === "object" ? data.percent : undefined;
        const rejection = rejectionOf(
          await callSafely(this.onVideoEndedExternal, {
            patient_id: this.patient_id,
            item: this.item,
            viewPercent:
              viewPercent !== undefined ? viewPercent.toFixed(2) : undefined,
          })
        );

        // «Спасибо за просмотр» — только когда сессия действительно записана.
        // Раньше это показывалось всегда, в том числе поверх отказа сервера.
        if (rejection) {
          this.writeError = rejection.text;
          return;
        }
      }

      this.videoEnded = true;
    },

    formatTime(viewProgress) {
      if (viewProgress === undefined) return undefined;
      const roundedSeconds = Math.round(viewProgress);
      const minutes = Math.floor(roundedSeconds / 60);

      const remainingSeconds = roundedSeconds % 60;
      const formattedSeconds =
        remainingSeconds < 10 ? "0" + remainingSeconds : remainingSeconds;
      return `${minutes}:${formattedSeconds}`;
    },

    handleVideoAvailable() {
      // Видео стало доступно, можно обновить состояние
      this.$forceUpdate();
    },
    async updateViewProgress(data) {
      const viewProgress = typeof data === "object" ? data.currentTime : data;
      const viewPercent = typeof data === "object" ? data.percent : undefined;

      if (this.modalFormComponent && this.modalFormComponent.item) {
        this.modalFormComponent.item.viewProgress = viewProgress.toFixed(3);
        this.modalFormComponent.item.viewProgressFormatted =
          this.formatTime(viewProgress);
      }

      if (typeof this.onUpdateViewProgressExternal !== "function") return;

      const rejection = rejectionOf(
        await callSafely(this.onUpdateViewProgressExternal, {
          patient_id: this.patient_id,
          item: this.item,
          watchtime: viewProgress.toFixed(2),
          viewPercent:
            viewPercent !== undefined ? viewPercent.toFixed(2) : undefined,
        })
      );

      // Останавливаем просмотр только на 409. Разовая сетевая ошибка — не повод
      // выкидывать сотрудника из сессии: watchtime шлётся каждые несколько
      // секунд и следующий вызов, скорее всего, дойдёт.
      if (rejection?.fatal) this.writeError = rejection.text;
    },
  },
  beforeMount() {
    this.request_products();
  },
  beforeDestroy() {
    // Вызываем onDestroy из modalFormComponent, если он передан
    if (
      this.modalFormComponent?.onDestroy &&
      typeof this.modalFormComponent.onDestroy === "function"
    ) {
      try {
        this.modalFormComponent.onDestroy();
      } catch (error) {
        console.error("Ошибка при вызове onDestroy:", error);
      }
    }
  },
};
</script>

<style scoped lang="scss">
.player {
  align-items: center;
  justify-content: center;
  text-align: center;
  display: flex;
  margin: auto 10px;
}
</style>
